import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CHOICE_LETTERS,
  DEFAULT_MODIFIERS,
  DEFAULT_TOP_PRIZE,
  DIFFICULTIES,
  formatMoney,
  MAX_PLAYER_AGE,
  MIN_PLAYER_AGE,
  PHONE_FRIEND_SECONDS
} from "./millionaireConstants";
import {
  advanceAfterCorrect,
  cancelPending,
  confirmFinalAnswer,
  createGame,
  currentQuestion,
  endPhoneFriend,
  selectAnswer,
  startPhoneFriend,
  beginPhoneFriendTimer,
  tickPhoneFriend,
  tickTimer,
  applyAudience,
  applyFiftyFifty,
  walkAway
} from "./millionaireEngine";
import { getFallbackQuestions } from "./millionaireQuestionBank";
import { millionaireAudio } from "./millionaireAudio";
import "./MillionaireGamePage.css";

const LOADING_TIPS = [
  "Building your money tree…",
  "Writing questions just for you…",
  "Tuning difficulty to your age…",
  "Polishing those tricky final five…",
  "Almost ready for the hot seat…"
];

function filterFamily(familyMembers) {
  const seen = new Set();
  const out = [];
  for (const m of familyMembers || []) {
    const id = String(m?.id || "").trim();
    const name = String(m?.name || "").trim();
    if (!id || !name || id === "all") continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name, color: m.color });
  }
  return out;
}

function MoneyTree({
  ladder,
  questionIndex,
  clearedThrough,
  celebrateIndex = null,
  fullScreen = false
}) {
  const amounts = ladder?.amounts || [];
  const rows = amounts
    .map((amount, i) => ({ amount, i, q: i + 1 }))
    .slice()
    .reverse();

  return (
    <ol
      className={`mm-tree${fullScreen ? " mm-tree-fullscreen" : ""}`}
      aria-label="Money tree"
    >
      {rows.map(({ amount, i, q }) => {
        const isSafe = ladder.safeIndices?.includes(i);
        const isCleared = i <= clearedThrough;
        const isCurrent = i === questionIndex && !isCleared;
        const isCelebrate = celebrateIndex === i;
        return (
          <li
            key={q}
            className={[
              "mm-tree-row",
              isSafe || i === amounts.length - 1 ? "mm-tree-safe" : "",
              isCurrent ? "mm-tree-current" : "",
              isCleared ? "mm-tree-cleared" : "",
              isCelebrate ? "mm-tree-celebrate" : ""
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span className="mm-tree-q">{q}</span>
            <span className="mm-tree-lead" aria-hidden="true" />
            <span className="mm-tree-amount">{formatMoney(amount)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function LifelineButton({ label, icon, available, used, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`mm-lifeline${available ? "" : " mm-lifeline-off"}${used ? " mm-lifeline-used" : ""}`}
      onClick={onClick}
      disabled={disabled || !available || used}
      aria-label={label}
      title={label}
    >
      <span className="mm-lifeline-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="mm-lifeline-label">{label}</span>
    </button>
  );
}

export default function MillionaireGamePage({ apiBase, familyMembers, onBack }) {
  const family = useMemo(() => filterFamily(familyMembers), [familyMembers]);

  const [phase, setPhase] = useState("setup"); // setup | loading | play | result
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [topPrizeInput, setTopPrizeInput] = useState(String(DEFAULT_TOP_PRIZE));
  const [modifiers, setModifiers] = useState({ ...DEFAULT_MODIFIERS });
  const [error, setError] = useState(null);
  const [game, setGame] = useState(null);
  const [tipIndex, setTipIndex] = useState(0);
  const [questionEnterKey, setQuestionEnterKey] = useState(0);
  const [suspense, setSuspense] = useState(false);
  /** ladder = full-screen tree; question = modal open */
  const [playView, setPlayView] = useState("ladder");
  /** After reveal, wait ~1s then show Continue */
  const [showContinue, setShowContinue] = useState(false);
  const [closingQuestion, setClosingQuestion] = useState(false);
  const [celebrateIndex, setCelebrateIndex] = useState(null);
  const abortRef = useRef(null);
  const continueDelayRef = useRef(null);
  const finalDelayRef = useRef(null);
  const [finalPromptReady, setFinalPromptReady] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() => millionaireAudio.isMuted());
  const prevPhaseRef = useRef("setup");
  const lastRevealKeyRef = useRef("");
  const lastTickRef = useRef(null);

  const topPrize = useMemo(() => {
    const n = Number(String(topPrizeInput).replace(/[,$]/g, ""));
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_TOP_PRIZE;
    return n;
  }, [topPrizeInput]);

  useEffect(() => {
    if (phase !== "loading") return undefined;
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % LOADING_TIPS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [phase]);

  // Timer only while the question modal is active (not on ladder / final-answer / reveal)
  const timerActive =
    phase === "play" &&
    playView === "question" &&
    game?.status === "playing" &&
    !game?.revealCorrect;

  useEffect(() => {
    if (!timerActive) return undefined;
    const id = setInterval(() => {
      setGame((prev) => {
        if (!prev || prev.status !== "playing") return prev;
        let next = prev;
        if (prev.phoneFriendActive) {
          next = tickPhoneFriend(next);
        }
        next = tickTimer(next);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timerActive]);

  // After answer reveal: brief beat, then show Continue
  useEffect(() => {
    if (!game?.revealCorrect || playView !== "question") {
      setShowContinue(false);
      return undefined;
    }
    setShowContinue(false);
    continueDelayRef.current = setTimeout(() => setShowContinue(true), 1000);
    return () => {
      if (continueDelayRef.current) clearTimeout(continueDelayRef.current);
    };
  }, [game?.revealCorrect, game?.lockedChoice, game?.questionIndex, playView]);

  // Let the selected answer fill gold before the "final answer?" prompt
  useEffect(() => {
    if (game?.pendingChoice == null || playView !== "question" || game?.revealCorrect) {
      setFinalPromptReady(false);
      return undefined;
    }
    setFinalPromptReady(false);
    finalDelayRef.current = setTimeout(() => setFinalPromptReady(true), 1100);
    return () => {
      if (finalDelayRef.current) clearTimeout(finalDelayRef.current);
    };
  }, [game?.pendingChoice, playView, game?.revealCorrect]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (continueDelayRef.current) clearTimeout(continueDelayRef.current);
      if (finalDelayRef.current) clearTimeout(finalDelayRef.current);
      millionaireAudio.stopAll();
    };
  }, []);

  useEffect(() => {
    const unlock = () => millionaireAudio.unlock();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    if (phase === "loading") {
      millionaireAudio.unlock();
      millionaireAudio.startLoadingPulse();
      return () => millionaireAudio.stopLoadingPulse();
    }
    return undefined;
  }, [phase]);

  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (prev !== "play" && phase === "play") {
      millionaireAudio.playContestantIntro();
    }
    if (phase !== "play" && phase !== "loading") {
      millionaireAudio.stopAll();
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (!game?.revealCorrect) {
      lastRevealKeyRef.current = "";
      return;
    }
    const key = `${game.questionIndex}:${game.status}:${game.lockedChoice}:${game.finalAmount}`;
    if (lastRevealKeyRef.current === key) return;
    lastRevealKeyRef.current = key;
    millionaireAudio.stopPhoneRing();
    if (game.status === "won") millionaireAudio.playWin();
    else if (game.status === "wrong") millionaireAudio.playWrong();
    else if (game.status === "timeout") millionaireAudio.playTimeout();
    else millionaireAudio.playCorrect(game.questionIndex);
  }, [game?.revealCorrect, game?.status, game?.lockedChoice, game?.questionIndex, game?.finalAmount]);

  useEffect(() => {
    if (game?.pendingChoice == null || game?.revealCorrect || playView !== "question") return;
    if (!finalPromptReady) return;
    millionaireAudio.playFinalPrompt();
  }, [finalPromptReady, game?.pendingChoice, game?.revealCorrect, playView]);

  useEffect(() => {
    if (game?.phoneFriendActive && !game.phoneFriendCounting) {
      millionaireAudio.playPhoneRing();
      return () => millionaireAudio.stopPhoneRing();
    }
    millionaireAudio.stopPhoneRing();
    return undefined;
  }, [game?.phoneFriendActive, game?.phoneFriendCounting]);

  useEffect(() => {
    lastTickRef.current = null;
  }, [game?.questionIndex]);

  useEffect(() => {
    const left = game?.timerSecondsLeft;
    if (
      !timerActive ||
      left == null ||
      left > 10 ||
      left <= 0 ||
      lastTickRef.current === left
    ) {
      return;
    }
    lastTickRef.current = left;
    millionaireAudio.playTick(left);
  }, [timerActive, game?.timerSecondsLeft]);

  useEffect(() => {
    if (playView !== "question") {
      millionaireAudio.stopQuestionAmbience(0.25);
      millionaireAudio.stopPhoneRing();
    }
  }, [playView]);

  useEffect(() => {
    if (celebrateIndex == null) return;
    millionaireAudio.playTreeCelebrate();
  }, [celebrateIndex]);

  function toggleSound() {
    millionaireAudio.unlock();
    const next = !soundMuted;
    millionaireAudio.setMuted(next);
    setSoundMuted(next);
  }

  function toggleModifier(key) {
    setModifiers((m) => ({ ...m, [key]: !m[key] }));
  }

  function pickFamilyMember(member) {
    setName(member.name);
  }

  async function startGame() {
    setError(null);
    const trimmed = name.trim();
    const ageNum = Number(age);
    if (!trimmed) {
      setError("Enter a player name.");
      return;
    }
    if (!Number.isFinite(ageNum) || ageNum < MIN_PLAYER_AGE || ageNum > MAX_PLAYER_AGE) {
      setError(`Enter an age between ${MIN_PLAYER_AGE} and ${MAX_PLAYER_AGE}.`);
      return;
    }

    millionaireAudio.unlock();
    setPhase("loading");
    setTipIndex(0);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    let questions;
    try {
      const res = await fetch(`${apiBase}/api/GenerateMillionaireQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: ac.signal,
        body: JSON.stringify({
          playerName: trimmed,
          age: ageNum,
          difficulty,
          topPrize,
          count: 15,
          modifiers: {
            extraHardFinal5: modifiers.extraHardFinal5
          }
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not generate questions (${res.status})`);
      if (!Array.isArray(data.questions) || data.questions.length < 15) {
        throw new Error("Not enough questions returned");
      }
      questions = data.questions;
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("Millionaire Grok failed, using fallback bank", err);
      questions = getFallbackQuestions();
      setError(null);
    }

    try {
      const next = createGame({
        player: { name: trimmed, age: ageNum },
        questions,
        topPrize,
        modifiers
      });
      setGame(next);
      setQuestionEnterKey(0);
      setSuspense(false);
      setPlayView("ladder");
      setShowContinue(false);
      setClosingQuestion(false);
      setCelebrateIndex(null);
      setPhase("play");
    } catch (err) {
      setError(err.message || "Could not start game");
      setPhase("setup");
    }
  }

  function handleBack() {
    if (phase === "setup") {
      onBack?.();
      return;
    }
    if (phase === "loading") {
      abortRef.current?.abort();
      setPhase("setup");
      return;
    }
    if (phase === "play" || phase === "result") {
      const leave = window.confirm("Leave the hot seat and return to setup?");
      if (leave) {
        millionaireAudio.stopAll();
        setGame(null);
        setPhase("setup");
      }
    }
  }

  function openQuestionModal() {
    setClosingQuestion(false);
    setShowContinue(false);
    setCelebrateIndex(null);
    setQuestionEnterKey((k) => k + 1);
    setPlayView("question");
    const qi = game?.questionIndex ?? 0;
    millionaireAudio.playLetsPlay(qi);
    millionaireAudio.startQuestionAmbience(qi);
  }

  function onSelectChoice(index) {
    if (suspense || showContinue) return;
    millionaireAudio.playLockIn();
    setGame((prev) => (prev ? selectAnswer(prev, index) : prev));
  }

  function onConfirmFinal() {
    setSuspense(true);
    millionaireAudio.playSuspense();
    setTimeout(() => {
      setGame((prev) => (prev ? confirmFinalAnswer(prev) : prev));
      setSuspense(false);
    }, 700);
  }

  function onCancelFinal() {
    millionaireAudio.duckAmbience(false);
    setGame((prev) => (prev ? cancelPending(prev) : prev));
  }

  function onContinueAfterReveal() {
    if (!game) return;
    const status = game.status;

    if (status === "wrong" || status === "timeout" || status === "walked" || status === "won") {
      setClosingQuestion(true);
      setTimeout(() => {
        setPlayView("ladder");
        setClosingQuestion(false);
        setPhase("result");
      }, 380);
      return;
    }

    // Correct (mid-game): animate back to ladder, celebrate tier, wait for Next question
    const cleared = game.questionIndex;
    setClosingQuestion(true);
    setTimeout(() => {
      setPlayView("ladder");
      setClosingQuestion(false);
      setShowContinue(false);
      setCelebrateIndex(cleared);
      setTimeout(() => setCelebrateIndex(null), 1200);
    }, 380);
  }

  function onNextQuestion() {
    setGame((prev) => {
      if (!prev) return prev;
      return advanceAfterCorrect(prev);
    });
    // Open next question after a short beat so ladder update is visible
    setTimeout(() => openQuestionModal(), 280);
  }

  const q = game ? currentQuestion(game) : null;
  const showFinalModal = Boolean(
    game?.pendingChoice != null &&
      !game.revealCorrect &&
      !suspense &&
      playView === "question" &&
      finalPromptReady
  );
  const onLadderAfterCorrect =
    playView === "ladder" &&
    game?.status === "playing" &&
    game?.revealCorrect &&
    game?.lockedChoice != null &&
    game.questionIndex < 14;
  const canStartFirstQuestion =
    playView === "ladder" &&
    game?.status === "playing" &&
    !game?.revealCorrect &&
    game?.clearedThrough < 0;
  const walkAwayKeep =
    game?.revealCorrect
      ? game.currentWinnings
      : game && game.questionIndex > 0
        ? game.ladder.amounts[game.questionIndex - 1]
        : 0;

  return (
    <div className="mm-page">
      <header className="mm-header">
        <button type="button" className="mm-back-btn" onClick={handleBack}>
          Back
        </button>
        <h1 className="mm-title">Who Wants to Be a Millionaire?</h1>
        <button
          type="button"
          className="mm-sound-btn"
          onClick={toggleSound}
          aria-label={soundMuted ? "Unmute sound effects" : "Mute sound effects"}
          aria-pressed={!soundMuted}
          title={soundMuted ? "Unmute" : "Mute"}
        >
          {soundMuted ? (
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="currentColor"
                d="M4.3 3.3 3 4.6l4.2 4.2H3v6.4h3.4L12 21V12.8l5.4 5.4A8.3 8.3 0 0 1 14 19.7v2.2a10.5 10.5 0 0 0 5.1-2.5l1.8 1.8 1.3-1.3L4.3 3.3zM12 3 9.1 6.1 12 9V3zM19.1 9.4l-1.5 1.5a5.2 5.2 0 0 1 .2 1.1 5.4 5.4 0 0 1-2.8 4.7l1.5 1.5A7.4 7.4 0 0 0 20 12a7 7 0 0 0-.9-3.6z"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3 9.2v5.6h3.4L12 21V3L6.4 9.2H3zm13.2 2.8a4.4 4.4 0 0 0-2.2-3.8v7.6a4.4 4.4 0 0 0 2.2-3.8zm-2.2-8.3v2.2a7.4 7.4 0 0 1 0 12.2v2.2a9.6 9.6 0 0 0 0-16.6z"
              />
            </svg>
          )}
        </button>
      </header>

      {error ? (
        <p className="mm-error" role="alert">
          {error}
        </p>
      ) : null}

      {phase === "setup" && (
        <div className="mm-setup">
          <p className="mm-setup-lead">Enter the contestant, set the stakes, then take the hot seat.</p>

          <label className="mm-field">
            <span>Player name</span>
            <input
              className="mm-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={40}
              autoComplete="nickname"
            />
          </label>

          {family.length > 0 ? (
            <div className="mm-family-picks" aria-label="Quick pick family member">
              {family.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="mm-chip"
                  style={m.color ? { "--mm-chip": m.color } : undefined}
                  onClick={() => pickFamilyMember(m)}
                >
                  {m.name}
                </button>
              ))}
            </div>
          ) : null}

          <label className="mm-field">
            <span>Age</span>
            <input
              className="mm-input mm-input-narrow"
              type="number"
              min={MIN_PLAYER_AGE}
              max={MAX_PLAYER_AGE}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="e.g. 10"
            />
          </label>

          <div className="mm-field">
            <span>Difficulty</span>
            <div className="mm-choice-row">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`mm-choice${difficulty === d.id ? " mm-selected" : ""}`}
                  onClick={() => setDifficulty(d.id)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <label className="mm-field">
            <span>Top prize (default $1,000,000)</span>
            <input
              className="mm-input"
              value={topPrizeInput}
              onChange={(e) => setTopPrizeInput(e.target.value)}
              inputMode="decimal"
              placeholder="1000000"
            />
          </label>

          <div className="mm-modifiers">
            <p className="mm-modifiers-title">Game options</p>
            {[
              ["unlimitedTime", "Unlimited time (no question timer)"],
              ["showExplanations", "Show explanations after answers"],
              ["extraHardFinal5", "Extra hard final 5 questions"],
              ["disableFiftyFifty", "Disable 50:50 lifeline"],
              ["disablePhoneFriend", "Disable Phone-a-Friend"],
              ["disableAudience", "Disable Ask the Audience"]
            ].map(([key, label]) => (
              <label key={key} className="mm-check">
                <input
                  type="checkbox"
                  checked={Boolean(modifiers[key])}
                  onChange={() => toggleModifier(key)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <button type="button" className="mm-btn mm-btn-primary" onClick={startGame}>
            Start game
          </button>
        </div>
      )}

      {phase === "loading" && (
        <div className="mm-loading" aria-busy="true" aria-live="polite">
          <div className="mm-loading-rings" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <p className="mm-loading-title">Preparing the hot seat</p>
          <p className="mm-loading-tip">{LOADING_TIPS[tipIndex]}</p>
        </div>
      )}

      {phase === "play" && game && (
        <div className="mm-play mm-play-ladder-mode">
          <div className="mm-play-top">
            <div className="mm-contestant">
              <span className="mm-contestant-name">{game.player.name}</span>
              <span className="mm-contestant-bank">
                Bank: {formatMoney(game.currentWinnings)}
              </span>
            </div>
          </div>

          <div className={`mm-ladder-stage${playView === "question" ? " mm-ladder-dimmed" : ""}`}>
            <p className="mm-host" aria-live="polite">
              {playView === "ladder"
                ? onLadderAfterCorrect
                  ? "Correct! Ready for the next question?"
                  : canStartFirstQuestion
                    ? "Here's your money tree. Ready when you are."
                    : game.hostCaption
                : game.hostCaption}
            </p>

            <MoneyTree
              ladder={game.ladder}
              questionIndex={game.questionIndex}
              clearedThrough={game.clearedThrough}
              celebrateIndex={celebrateIndex}
              fullScreen
            />

            {playView === "ladder" && (
              <div className="mm-ladder-actions">
                {canStartFirstQuestion ? (
                  <button
                    type="button"
                    className="mm-btn mm-btn-primary mm-btn-next"
                    onClick={openQuestionModal}
                  >
                    Question 1 · {formatMoney(game.ladder.amounts[0])}
                  </button>
                ) : null}

                {onLadderAfterCorrect ? (
                  <button
                    type="button"
                    className="mm-btn mm-btn-primary mm-btn-next mm-btn-pulse"
                    onClick={onNextQuestion}
                  >
                    Next question ·{" "}
                    {formatMoney(game.ladder.amounts[game.questionIndex + 1])}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {playView === "question" && q ? (
            <div
              className={`mm-question-backdrop${closingQuestion ? " mm-question-closing" : " mm-question-opening"}`}
              role="dialog"
              aria-label="Question"
            >
              <div key={questionEnterKey} className="mm-question-modal">
                <div
                  className={`mm-timer mm-timer-modal${
                    game.timerSecondsLeft != null && game.timerSecondsLeft <= 10
                      ? " mm-timer-urgent"
                      : ""
                  }${game.timerPaused ? " mm-timer-paused" : ""}${
                    game.timerSecondsLeft == null ? " mm-timer-off" : ""
                  }`}
                  aria-label="Question timer"
                >
                  {game.timerSecondsLeft == null
                    ? "∞"
                    : `${game.timerPaused ? "PAUSED " : ""}${game.timerSecondsLeft}s`}
                </div>

                <div className="mm-lifelines">
                  <LifelineButton
                    label="50:50"
                    icon="50:50"
                    available={!modifiers.disableFiftyFifty}
                    used={!game.lifelines.fiftyFifty && !modifiers.disableFiftyFifty}
                    disabled={
                      game.revealCorrect ||
                      game.pendingChoice != null ||
                      game.phoneFriendActive ||
                      suspense
                    }
                    onClick={() => {
                      millionaireAudio.playFiftyFifty();
                      setGame((prev) => (prev ? applyFiftyFifty(prev) : prev));
                    }}
                  />
                  <LifelineButton
                    label="Phone-a-Friend"
                    icon="CALL"
                    available={!modifiers.disablePhoneFriend}
                    used={!game.lifelines.phoneFriend && !modifiers.disablePhoneFriend}
                    disabled={
                      game.revealCorrect ||
                      game.pendingChoice != null ||
                      game.phoneFriendActive ||
                      suspense
                    }
                    onClick={() => setGame((prev) => (prev ? startPhoneFriend(prev) : prev))}
                  />
                  <LifelineButton
                    label="Ask the Audience"
                    icon="POLL"
                    available={!modifiers.disableAudience}
                    used={!game.lifelines.audience && !modifiers.disableAudience}
                    disabled={
                      game.revealCorrect ||
                      game.pendingChoice != null ||
                      game.phoneFriendActive ||
                      suspense
                    }
                    onClick={() => {
                      millionaireAudio.playAudience();
                      setGame((prev) => (prev ? applyAudience(prev) : prev));
                    }}
                  />
                </div>

                <div className="mm-question-plate">
                  <p className="mm-question-value">
                    Question {game.questionIndex + 1} ·{" "}
                    {formatMoney(game.ladder.amounts[game.questionIndex])}
                  </p>
                  <h2 className="mm-question-text">{q.prompt}</h2>
                </div>

                {game.audiencePercents ? (
                  <div className="mm-audience" aria-label="Audience results">
                    {game.audiencePercents.map((pct, i) =>
                      game.removedChoices.includes(i) ? null : (
                        <div key={i} className="mm-audience-bar-wrap">
                          <span>{CHOICE_LETTERS[i]}</span>
                          <div className="mm-audience-track">
                            <div
                              className="mm-audience-fill"
                              style={{ width: `${Math.max(0, pct)}%` }}
                            />
                          </div>
                          <span>{pct}%</span>
                        </div>
                      )
                    )}
                  </div>
                ) : null}

                <div className="mm-answers">
                  {q.choices.map((text, i) => {
                    if (game.removedChoices.includes(i)) {
                      return (
                        <div key={i} className="mm-answer mm-answer-removed" aria-hidden="true">
                          <span className="mm-answer-letter">{CHOICE_LETTERS[i]}:</span>
                          <span className="mm-answer-text">—</span>
                        </div>
                      );
                    }
                    const isPending = game.pendingChoice === i;
                    const isLocked = game.lockedChoice === i;
                    const isCorrect = game.revealCorrect && i === q.correctIndex;
                    const isWrong =
                      game.revealCorrect &&
                      isLocked &&
                      game.lockedChoice !== q.correctIndex;
                    return (
                      <button
                        key={i}
                        type="button"
                        className={[
                          "mm-answer",
                          isPending ? "mm-answer-pending" : "",
                          isCorrect ? "mm-answer-correct" : "",
                          isWrong ? "mm-answer-wrong" : "",
                          suspense && isPending ? "mm-answer-suspense" : ""
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        disabled={
                          game.revealCorrect ||
                          game.pendingChoice != null ||
                          game.phoneFriendActive ||
                          suspense ||
                          game.status !== "playing"
                        }
                        onClick={() => onSelectChoice(i)}
                      >
                        <span className="mm-answer-letter">{CHOICE_LETTERS[i]}:</span>
                        <span className="mm-answer-text">{text}</span>
                      </button>
                    );
                  })}
                </div>

                {game.revealCorrect &&
                modifiers.showExplanations &&
                q.explanation ? (
                  <p className="mm-explanation">{q.explanation}</p>
                ) : null}

                {game.revealCorrect && showContinue ? (
                  <div className="mm-continue-wrap">
                    <button
                      type="button"
                      className="mm-btn mm-btn-primary mm-btn-pulse"
                      onClick={onContinueAfterReveal}
                    >
                      Continue
                    </button>
                  </div>
                ) : null}

                {!game.revealCorrect ? (
                  <div className="mm-play-actions">
                    <button
                      type="button"
                      className="mm-btn mm-btn-walk"
                      disabled={
                        game.pendingChoice != null ||
                        game.phoneFriendActive ||
                        suspense
                      }
                      onClick={() => {
                        if (window.confirm("Walk away and keep your current winnings?")) {
                          millionaireAudio.playWalkAway();
                          setGame((prev) => (prev ? walkAway(prev) : prev));
                          setPhase("result");
                        }
                      }}
                    >
                      Walk away ({formatMoney(walkAwayKeep)})
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {game.phoneFriendActive && playView === "question" && q ? (
            <div className="mm-modal-backdrop" role="dialog" aria-label="Phone a Friend">
              <div className="mm-modal">
                <h2>Phone-a-Friend</h2>
                <p>
                  {game.phoneFriendCounting
                    ? `Ask your friend — you have ${PHONE_FRIEND_SECONDS} seconds.`
                    : "Get your friend on the phone. Start the timer when they're ready."}
                </p>
                <p className={`mm-phone-count${game.phoneFriendCounting ? "" : " mm-phone-count-waiting"}`}>
                  {game.phoneFriendSecondsLeft}s
                </p>
                <p className="mm-question-text mm-phone-q">{q.prompt}</p>
                <ul className="mm-phone-choices">
                  {q.choices.map((c, i) =>
                    game.removedChoices.includes(i) ? null : (
                      <li key={i}>
                        <strong>{CHOICE_LETTERS[i]}:</strong> {c}
                      </li>
                    )
                  )}
                </ul>
                <div className="mm-modal-actions">
                  {!game.phoneFriendCounting ? (
                    <button
                      type="button"
                      className="mm-btn mm-btn-primary"
                      onClick={() => setGame((prev) => (prev ? beginPhoneFriendTimer(prev) : prev))}
                    >
                      Start timer
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`mm-btn${game.phoneFriendCounting ? " mm-btn-primary" : ""}`}
                    onClick={() => setGame((prev) => (prev ? endPhoneFriend(prev) : prev))}
                  >
                    End call
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {showFinalModal && q ? (
            <div className="mm-modal-backdrop mm-final-backdrop" role="dialog" aria-label="Final answer">
              <div className="mm-modal">
                <h2>Is that your final answer?</h2>
                <p className="mm-final-choice">
                  <strong>{CHOICE_LETTERS[game.pendingChoice]}:</strong>{" "}
                  {q.choices[game.pendingChoice]}
                </p>
                <div className="mm-modal-actions">
                  <button type="button" className="mm-btn" onClick={onCancelFinal}>
                    No
                  </button>
                  <button type="button" className="mm-btn mm-btn-primary" onClick={onConfirmFinal}>
                    Yes — final answer
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {phase === "result" && game && (
        <div className="mm-result">
          <p className="mm-result-kicker">
            {game.status === "won"
              ? "Congratulations!"
              : game.status === "walked"
                ? "Walking away"
                : game.status === "timeout"
                  ? "Time's up"
                  : "Game over"}
          </p>
          <h2 className="mm-result-title">{game.player.name}</h2>
          <p className="mm-result-amount">{formatMoney(game.finalAmount)}</p>
          <p className="mm-result-sub">
            {game.status === "won"
              ? "You climbed the entire money tree!"
              : game.status === "walked"
                ? "Smart play — you kept your winnings."
                : `You leave with ${formatMoney(game.finalAmount)}.`}
          </p>
          <div className="mm-result-actions">
            <button
              type="button"
              className="mm-btn mm-btn-primary"
              onClick={() => {
                millionaireAudio.stopAll();
                setGame(null);
                setPhase("setup");
              }}
            >
              Play again
            </button>
            <button type="button" className="mm-btn" onClick={() => {
              millionaireAudio.stopAll();
              onBack?.();
            }}>
              Games menu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
